import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Box, Button, IconButton, Link, Spinner, Stack, Text } from '@chakra-ui/react';
import { LuEye, LuEyeOff, LuX } from 'react-icons/lu';
import { useColorModeValue } from '../../components/ui/color-mode';
import { Tooltip as UiTooltip } from '../../components/ui/tooltip';

const NOTABLE_DATE_SNAP_PX = 28;
const NOTABLE_DATE_SNAP_MIN_MS = 3 * 24 * 60 * 60 * 1000;
const NOTABLE_DATE_SNAP_DEFAULT_PLOT_WIDTH_PX = 640;
const DRAG_COMPARE_LINE_COLOR = '#4A5568';
const DRAG_COMPARE_DOT_COLOR = '#2C7A7B';
const PRICE_LINE_COLOR = '#2C7A7B';
const VOLUME_BAR_COLOR = '#2C7A7B';
const DRAG_COMPARE_MIN_POINTER_DISTANCE_PX = 4;

export function getNotableDateSnapThresholdMs(
  xDomain,
  {
    plotWidthPx = NOTABLE_DATE_SNAP_DEFAULT_PLOT_WIDTH_PX,
    snapPx = NOTABLE_DATE_SNAP_PX,
  } = {},
) {
  const [domainStart, domainEnd] = Array.isArray(xDomain) ? xDomain : [];
  if (!Number.isFinite(domainStart) || !Number.isFinite(domainEnd) || domainEnd <= domainStart) {
    return 14 * 24 * 60 * 60 * 1000;
  }
  if (!Number.isFinite(plotWidthPx) || plotWidthPx <= 0) {
    return NOTABLE_DATE_SNAP_MIN_MS;
  }
  return Math.max(((domainEnd - domainStart) / plotWidthPx) * snapPx, NOTABLE_DATE_SNAP_MIN_MS);
}

export function findNearestNotableDateKey(cursorTimestamp, markers, thresholdMs) {
  if (!Number.isFinite(cursorTimestamp) || !Array.isArray(markers) || markers.length === 0) {
    return null;
  }
  if (!Number.isFinite(thresholdMs) || thresholdMs < 0) {
    return null;
  }

  let nearestKey = null;
  let nearestDistance = Infinity;
  for (const marker of markers) {
    const timestamp = Number(marker?.timestamp);
    if (!Number.isFinite(timestamp)) continue;
    const distance = Math.abs(timestamp - cursorTimestamp);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestKey = marker.key;
    }
  }

  return nearestDistance <= thresholdMs ? nearestKey : null;
}

export function findNearestChartPoint(cursorTimestamp, chartData) {
  if (!Number.isFinite(cursorTimestamp) || !Array.isArray(chartData) || chartData.length === 0) {
    return null;
  }

  let low = 0;
  let high = chartData.length - 1;

  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    const midTs = Number(chartData[mid]?.dateMs);
    if (!Number.isFinite(midTs) || midTs < cursorTimestamp) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  let bestIndex = low;
  let bestDistance = Math.abs(Number(chartData[bestIndex]?.dateMs) - cursorTimestamp);

  if (low > 0) {
    const previousDistance = Math.abs(Number(chartData[low - 1]?.dateMs) - cursorTimestamp);
    if (previousDistance <= bestDistance) {
      bestIndex = low - 1;
      bestDistance = previousDistance;
    }
  }

  const point = chartData[bestIndex];
  if (!point || !Number.isFinite(Number(point.dateMs)) || !Number.isFinite(Number(point.price))) {
    return null;
  }

  return { ...point, index: bestIndex };
}

export function isSignificantDragCompare(
  startPoint,
  endPoint,
  pointerDistance = DRAG_COMPARE_MIN_POINTER_DISTANCE_PX,
) {
  if (!startPoint || !endPoint) return false;
  const startTs = Number(startPoint.dateMs);
  const endTs = Number(endPoint.dateMs);
  return Number.isFinite(startTs)
    && Number.isFinite(endTs)
    && startTs !== endTs
    && Number.isFinite(pointerDistance)
    && pointerDistance >= DRAG_COMPARE_MIN_POINTER_DISTANCE_PX;
}

export function computeDragCompareDelta(startPoint, endPoint) {
  if (!startPoint || !endPoint) return null;

  const startTs = Number(startPoint.dateMs);
  const endTs = Number(endPoint.dateMs);
  if (!Number.isFinite(startTs) || !Number.isFinite(endTs)) {
    return null;
  }

  // Always measure left→right on the timeline, regardless of drag direction.
  const earlier = startTs <= endTs ? startPoint : endPoint;
  const later = startTs <= endTs ? endPoint : startPoint;

  const earlierPrice = Number(earlier.price);
  const laterPrice = Number(later.price);
  if (!Number.isFinite(earlierPrice) || !Number.isFinite(laterPrice)) {
    return null;
  }

  const absoluteDelta = laterPrice - earlierPrice;
  const percentDelta = earlierPrice === 0 ? null : (absoluteDelta / earlierPrice) * 100;
  let direction;
  if (absoluteDelta > 0) {
    direction = 'up';
  } else if (absoluteDelta < 0) {
    direction = 'down';
  } else {
    direction = 'flat';
  }

  return {
    absoluteDelta,
    percentDelta,
    direction,
    startDate: earlier.date || null,
    endDate: later.date || null,
    startPrice: earlierPrice,
    endPrice: laterPrice,
    startMs: Number(earlier.dateMs),
    endMs: Number(later.dateMs),
  };
}

const DRAG_COMPARE_DELTA_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'always',
});

const DRAG_COMPARE_PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'always',
});

const DRAG_COMPARE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatDragCompareDate(value) {
  if (Number.isFinite(Number(value))) {
    return DRAG_COMPARE_DATE_FORMATTER.format(new Date(Number(value)));
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
    return null;
  }

  const timestamp = Date.parse(`${value}T00:00:00Z`);
  if (!Number.isFinite(timestamp)) {
    return null;
  }

  return DRAG_COMPARE_DATE_FORMATTER.format(new Date(timestamp));
}

export function formatDragCompareBadge(delta) {
  if (!delta || !Number.isFinite(delta.absoluteDelta)) {
    return null;
  }

  const absoluteText = DRAG_COMPARE_DELTA_FORMATTER.format(delta.absoluteDelta);
  const percentText = Number.isFinite(delta.percentDelta)
    ? ` (${DRAG_COMPARE_PERCENT_FORMATTER.format(delta.percentDelta)}%)`
    : '';
  const deltaText = `${absoluteText}${percentText}`;
  const startLabel = formatDragCompareDate(delta.startMs ?? delta.startDate);
  const endLabel = formatDragCompareDate(delta.endMs ?? delta.endDate);
  const dateText = startLabel && endLabel ? `${startLabel} – ${endLabel}` : null;

  return { deltaText, dateText, direction: delta.direction || 'flat' };
}

export function getDragCompareBadgeLeftPercent(startMs, endMs, xDomain) {
  const [domainStart, domainEnd] = Array.isArray(xDomain) ? xDomain : [];
  if (
    !Number.isFinite(startMs)
    || !Number.isFinite(endMs)
    || !Number.isFinite(domainStart)
    || !Number.isFinite(domainEnd)
    || domainEnd <= domainStart
  ) {
    return 50;
  }

  const midMs = (startMs + endMs) / 2;
  const percent = ((midMs - domainStart) / (domainEnd - domainStart)) * 100;
  return Math.min(92, Math.max(8, percent));
}

function getNotableDateLabelPosition(timestamp, xDomain) {
  const [domainStart, domainEnd] = Array.isArray(xDomain) ? xDomain : [];
  if (!Number.isFinite(domainStart) || !Number.isFinite(domainEnd) || domainEnd <= domainStart) {
    return 'insideTopLeft';
  }

  const rightSideThreshold = domainStart + ((domainEnd - domainStart) * 0.75);
  return timestamp >= rightSideThreshold ? 'insideTopRight' : 'insideTopLeft';
}

const StockPriceHistoryRechartsShell = lazy(() =>
  import('recharts').then(({
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ReferenceLine,
    ReferenceArea,
    ReferenceDot,
  }) => ({
    default: function StockPriceHistoryRechartsShell({
      chartData,
      gridStroke,
      axisColor,
      formatPriceTick,
      formatDateTick,
      formatVolumeTick: formatVolumeAxisTick,
      tooltipRenderer,
      referenceDate,
      referenceLabel,
      referenceTimestamp,
      selectedPoint = null,
      notableDateMarkers,
      hiddenNotableDateKeys,
      hoveredNotableDateKey,
      onNotableDateHover,
      onPointClick = null,
      xDomain,
      lineType = 'linear',
    }) {
      const snapThresholdMs = getNotableDateSnapThresholdMs(xDomain);
      const visibleNotableDateMarkers = (notableDateMarkers || []).filter(
        (marker) => !hiddenNotableDateKeys?.has?.(marker.key),
      );
      const containerRef = useRef(null);
      const dragStartRef = useRef(null);
      const [dragDraft, setDragDraft] = useState(null);
      const [dragSelection, setDragSelection] = useState(null);
      const hasVolume = (chartData || []).some(
        (point) => Number.isFinite(Number(point?.volume)),
      );

      const activeCompare = dragDraft || dragSelection;
      const isDraggingOrSelected = Boolean(activeCompare);
      const pointerDistance = activeCompare
        ? Math.abs(Number(activeCompare.endX) - Number(activeCompare.startX))
        : 0;
      const compareDelta = activeCompare
        && isSignificantDragCompare(activeCompare.start, activeCompare.end, pointerDistance)
        ? computeDragCompareDelta(activeCompare.start, activeCompare.end)
        : null;
      const compareBadge = formatDragCompareBadge(compareDelta);

      const clearCompare = useCallback(() => {
        dragStartRef.current = null;
        setDragDraft(null);
        setDragSelection(null);
      }, []);

      const resolvePointFromEvent = useCallback((state) => {
        const cursorTimestamp = Number(
          state?.activeLabel
          ?? state?.activePayload?.[0]?.payload?.dateMs
          ?? state?.activeCoordinate?.x,
        );
        return findNearestChartPoint(cursorTimestamp, chartData);
      }, [chartData]);

      useEffect(() => {
        if (!isDraggingOrSelected && !selectedPoint) return undefined;

        const handleKeyDown = (event) => {
          if (event.key === 'Escape') {
            clearCompare();
            onNotableDateHover?.(null);
            onPointClick?.(null);
          }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
      }, [clearCompare, isDraggingOrSelected, onNotableDateHover, onPointClick, selectedPoint]);

      useEffect(() => {
        if (!dragSelection) return undefined;

        const handlePointerDown = (event) => {
          if (!containerRef.current?.contains(event.target)) {
            clearCompare();
            onNotableDateHover?.(null);
          }
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
      }, [clearCompare, dragSelection, onNotableDateHover]);

      useEffect(() => {
        if (!dragDraft) return undefined;

        const handleWindowMouseUp = () => {
          const draft = dragStartRef.current;
          if (!draft?.start || !draft?.end) {
            clearCompare();
            return;
          }

          const draftPointerDistance = Math.abs(Number(draft.endX) - Number(draft.startX));
          if (isSignificantDragCompare(draft.start, draft.end, draftPointerDistance)) {
            setDragSelection({
              start: draft.start,
              end: draft.end,
              startX: draft.startX,
              endX: draft.endX,
            });
            setDragDraft(null);
            dragStartRef.current = null;
            onNotableDateHover?.(null);
            onPointClick?.(null);
            return;
          }

          const clickPoint = draft.end || draft.start;
          clearCompare();
          if (onPointClick && clickPoint?.date) {
            onPointClick(clickPoint);
          }
        };

        window.addEventListener('mouseup', handleWindowMouseUp);
        return () => window.removeEventListener('mouseup', handleWindowMouseUp);
      }, [clearCompare, dragDraft, onNotableDateHover, onPointClick]);

      const handleChartMouseDown = (state, event) => {
        event?.preventDefault?.();
        window.getSelection?.()?.removeAllRanges?.();

        if (state?.activeTooltipIndex == null && state?.activeLabel == null) {
          return;
        }

        const point = resolvePointFromEvent(state);
        if (!point) return;
        const pointerX = Number(state?.chartX);
        if (!Number.isFinite(pointerX)) return;

        if (dragSelection) {
          clearCompare();
        }

        const nextDraft = {
          start: point,
          end: point,
          startX: pointerX,
          endX: pointerX,
        };
        dragStartRef.current = nextDraft;
        setDragDraft(nextDraft);
        setDragSelection(null);
        onNotableDateHover?.(null);
      };

      const handleChartMouseMove = (state) => {
        if (dragStartRef.current) {
          const point = resolvePointFromEvent(state);
          if (!point) return;
          const pointerX = Number(state?.chartX);
          if (!Number.isFinite(pointerX)) return;
          const nextDraft = {
            start: dragStartRef.current.start,
            end: point,
            startX: dragStartRef.current.startX,
            endX: pointerX,
          };
          dragStartRef.current = nextDraft;
          setDragDraft(nextDraft);
          onNotableDateHover?.(null);
          return;
        }

        if (dragSelection) {
          onNotableDateHover?.(null);
          return;
        }

        if (!visibleNotableDateMarkers.length) return;
        const cursorTimestamp = Number(state?.activeLabel);
        const nextKey = findNearestNotableDateKey(
          cursorTimestamp,
          visibleNotableDateMarkers,
          snapThresholdMs,
        );
        onNotableDateHover(nextKey);
      };

      const handleChartMouseLeave = () => {
        if (!dragStartRef.current && !dragSelection) {
          onNotableDateHover(null);
        }
      };

      const areaX1 = compareDelta
        ? Math.min(compareDelta.startMs, compareDelta.endMs)
        : null;
      const areaX2 = compareDelta
        ? Math.max(compareDelta.startMs, compareDelta.endMs)
        : null;
      const areaFill = compareDelta?.direction === 'up'
        ? '#C6F6D5'
        : compareDelta?.direction === 'down'
          ? '#FED7D7'
          : '#E2E8F0';
      const badgeColors = compareDelta?.direction === 'up'
        ? { bg: '#C6F6D5', color: '#22543D', border: '#68D391' }
        : compareDelta?.direction === 'down'
          ? { bg: '#FED7D7', color: '#822727', border: '#FC8181' }
          : { bg: '#EDF2F7', color: '#2D3748', border: '#CBD5E0' };
      const badgeLeftPercent = compareDelta
        ? getDragCompareBadgeLeftPercent(compareDelta.startMs, compareDelta.endMs, xDomain)
        : 50;
      const selectedTimestamp = Number.isFinite(Number(selectedPoint?.dateMs))
        ? Number(selectedPoint.dateMs)
        : null;
      const selectedPrice = Number.isFinite(Number(selectedPoint?.price))
        ? Number(selectedPoint.price)
        : null;

      return (
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            userSelect: 'none',
            WebkitUserSelect: 'none',
          }}
          data-testid="stock-price-drag-compare-root"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: hasVolume ? 12 : 8, left: 0, bottom: 8 }}
              onMouseDown={handleChartMouseDown}
              onMouseMove={handleChartMouseMove}
              onMouseLeave={handleChartMouseLeave}
              style={{ cursor: dragDraft ? 'col-resize' : 'crosshair' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis
                dataKey="dateMs"
                type="number"
                scale="time"
                domain={xDomain}
                tickFormatter={formatDateTick}
                tick={{ fontSize: 12, fill: axisColor }}
                tickMargin={8}
                minTickGap={28}
              />
              <YAxis
                yAxisId="price"
                dataKey="price"
                tickFormatter={formatPriceTick}
                tick={{ fontSize: 12, fill: axisColor }}
                tickMargin={6}
                width={68}
                domain={['auto', 'auto']}
              />
              {hasVolume ? (
                <YAxis
                  yAxisId="volume"
                  orientation="right"
                  dataKey="volume"
                  tickFormatter={formatVolumeAxisTick}
                  tick={{ fontSize: 11, fill: axisColor }}
                  tickMargin={4}
                  width={48}
                  domain={[0, 'auto']}
                />
              ) : null}
              <Tooltip content={isDraggingOrSelected ? () => null : tooltipRenderer} />
              {areaX1 !== null && areaX2 !== null ? (
                <ReferenceArea
                  yAxisId="price"
                  x1={areaX1}
                  x2={areaX2}
                  fill={areaFill}
                  fillOpacity={0.45}
                  ifOverflow="extendDomain"
                />
              ) : null}
              {hasVolume ? (
                <Bar
                  yAxisId="volume"
                  dataKey="volume"
                  name="Volume"
                  fill={VOLUME_BAR_COLOR}
                  fillOpacity={0.22}
                  maxBarSize={10}
                  isAnimationActive={false}
                />
              ) : null}
              {compareDelta ? (
                <ReferenceLine
                  yAxisId="price"
                  x={compareDelta.startMs}
                  stroke={DRAG_COMPARE_LINE_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                />
              ) : null}
              {compareDelta ? (
                <ReferenceLine
                  yAxisId="price"
                  x={compareDelta.endMs}
                  stroke={DRAG_COMPARE_LINE_COLOR}
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                />
              ) : null}
              {referenceTimestamp ? (
                <ReferenceLine
                  yAxisId="price"
                  x={referenceTimestamp}
                  stroke="#E53E3E"
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  label={{
                    value: referenceLabel || 'Snapshot',
                    position: 'insideTopRight',
                    fill: '#E53E3E',
                    fontSize: 12,
                  }}
                />
              ) : null}
              {selectedTimestamp !== null && !compareDelta ? (
                <ReferenceLine
                  yAxisId="price"
                  x={selectedTimestamp}
                  stroke="#2C7A7B"
                  strokeWidth={1.5}
                  strokeDasharray="3 3"
                  ifOverflow="extendDomain"
                />
              ) : null}
              {visibleNotableDateMarkers.map((marker) => (
                <ReferenceLine
                  key={`hit-${marker.key}`}
                  yAxisId="price"
                  x={marker.timestamp}
                  stroke="transparent"
                  strokeWidth={NOTABLE_DATE_SNAP_PX * 2}
                  ifOverflow="extendDomain"
                  onMouseEnter={() => {
                    if (isDraggingOrSelected) return;
                    onNotableDateHover(marker.key);
                  }}
                  onMouseLeave={() => {
                    if (isDraggingOrSelected) return;
                    onNotableDateHover(null);
                  }}
                />
              ))}
              {(notableDateMarkers || []).map((marker) => {
                const isHidden = Boolean(hiddenNotableDateKeys?.has?.(marker.key));
                const isHovered = !isHidden
                  && !isDraggingOrSelected
                  && hoveredNotableDateKey === marker.key;
                return (
                  <ReferenceLine
                    key={marker.key}
                    yAxisId="price"
                    x={marker.timestamp}
                    stroke={isHidden ? 'transparent' : '#805AD5'}
                    strokeOpacity={isHidden ? 0 : 1}
                    strokeWidth={isHidden ? 0 : (isHovered ? 3 : 1.5)}
                    strokeDasharray={isHovered ? undefined : '3 3'}
                    label={isHovered ? {
                      value: marker.event_date,
                      position: getNotableDateLabelPosition(marker.timestamp, xDomain),
                      fill: '#6B46C1',
                      fontSize: 11,
                      fontWeight: 700,
                    } : undefined}
                    aria-label={`${marker.event_date}: ${marker.description}`}
                    title={`${marker.event_date}: ${marker.description}`}
                  />
                );
              })}
              <Line
                yAxisId="price"
                type={lineType}
                dataKey="price"
                name="Stock Price"
                stroke={PRICE_LINE_COLOR}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
              {compareDelta ? (
                <ReferenceDot
                  yAxisId="price"
                  x={compareDelta.startMs}
                  y={compareDelta.startPrice}
                  r={5}
                  fill={DRAG_COMPARE_DOT_COLOR}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                />
              ) : null}
              {compareDelta ? (
                <ReferenceDot
                  yAxisId="price"
                  x={compareDelta.endMs}
                  y={compareDelta.endPrice}
                  r={5}
                  fill={DRAG_COMPARE_DOT_COLOR}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                />
              ) : null}
              {selectedTimestamp !== null && selectedPrice !== null && !compareDelta ? (
                <ReferenceDot
                  yAxisId="price"
                  x={selectedTimestamp}
                  y={selectedPrice}
                  r={5}
                  fill={PRICE_LINE_COLOR}
                  stroke="#FFFFFF"
                  strokeWidth={2}
                  ifOverflow="extendDomain"
                />
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
          {compareBadge ? (
            <div
              data-testid="stock-price-drag-compare-badge"
              data-direction={compareBadge.direction}
              style={{
                position: 'absolute',
                top: 10,
                left: `${badgeLeftPercent}%`,
                transform: 'translateX(-50%)',
                zIndex: 2,
                pointerEvents: 'none',
                background: badgeColors.bg,
                color: badgeColors.color,
                border: `1px solid ${badgeColors.border}`,
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                padding: '6px 10px',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                fontSize: 12,
                lineHeight: 1.3,
              }}
            >
              <div style={{ fontWeight: 700 }}>{compareBadge.deltaText}</div>
              {compareBadge.dateText ? (
                <div style={{ fontSize: 11, opacity: 0.9 }}>{compareBadge.dateText}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    },
  })),
);

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function parseDateMs(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
    return null;
  }

  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatDateTick(value) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  return new Date(timestamp).toISOString().slice(0, 10);
}

const PRICE_TICK_WHOLE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const PRICE_TICK_DECIMAL_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

function formatPriceTick(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  if (Math.abs(numericValue) >= 1000) {
    return `$${Math.round(numericValue).toLocaleString()}`;
  }

  return (
    numericValue >= 100 ? PRICE_TICK_WHOLE_FORMATTER : PRICE_TICK_DECIMAL_FORMATTER
  ).format(numericValue);
}

function formatVolume(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Math.round(numericValue).toLocaleString('en-US');
}

export function formatVolumeTick(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  const absoluteValue = Math.abs(numericValue);
  if (absoluteValue >= 1e9) {
    return `${(numericValue / 1e9).toFixed(1)}B`;
  }
  if (absoluteValue >= 1e6) {
    return `${(numericValue / 1e6).toFixed(1)}M`;
  }
  if (absoluteValue >= 1e3) {
    return `${Math.round(numericValue / 1e3)}K`;
  }

  return String(Math.round(numericValue));
}

const TOOLTIP_PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatTooltipPrice(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return TOOLTIP_PRICE_FORMATTER.format(numericValue);
}

function getErrorMessage(error) {
  if (!error) {
    return 'Unable to load stock price history.';
  }

  if (typeof error === 'string') {
    return error;
  }

  return error.message || 'Unable to load stock price history.';
}

function getStatusCopy(priceStatus, priceMessage, redactionEnabled) {
  if (priceStatus === 'available' && priceMessage) {
    return {
      label: 'Company history starts here',
      detail: priceMessage,
      tone: 'blue',
    };
  }

  if (priceStatus === 'partial') {
    return {
      label: 'Coverage not verified',
      detail: priceMessage || 'Available prices are shown, but some dates may be missing.',
      tone: 'orange',
    };
  }

  if (priceStatus === 'unavailable') {
    return {
      label: redactionEnabled ? 'Pre-snapshot history unavailable' : 'Price history unavailable',
      detail: priceMessage || 'No usable prices were found for this range.',
      tone: 'gray',
    };
  }

  return null;
}

const EMPTY_PRICE_HISTORY = [];
const EMPTY_NOTABLE_DATES = [];

function normalizeNotableDateMarker(notableDate) {
  const timestamp = parseDateMs(notableDate?.event_date);
  if (timestamp === null) {
    return null;
  }

  const description = String(notableDate?.event_description || 'Notable event').trim();
  return {
    ...notableDate,
    key: String(notableDate?.id ?? `${notableDate?.event_date || ''}:${description}`),
    timestamp,
    description,
  };
}

export default function StockPriceHistoryChart({
  prices = EMPTY_PRICE_HISTORY,
  loading = false,
  error = null,
  redactionEnabled = false,
  preferAdjustedClose = true,
  priceStatus = null,
  priceMessage = null,
  referenceDate = null,
  referenceLabel = null,
  notableDates = EMPTY_NOTABLE_DATES,
  onRemoveNotableDate = null,
  onUseAsSnapshotDate = null,
  lineType = 'linear',
}) {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const panelBg = useColorModeValue('white', 'gray.900');
  const emptyBg = useColorModeValue('gray.50', 'gray.800');
  const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
  const axisColor = useColorModeValue('#4A5568', '#CBD5E0');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipText = useColorModeValue('gray.800', 'gray.100');
  const tooltipMuted = useColorModeValue('gray.600', 'gray.400');
  const notableHoverBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const notableMutedBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const snapshotPromptBg = useColorModeValue('teal.50', 'teal.950');
  const snapshotPromptBorder = useColorModeValue('teal.200', 'teal.800');
  const [hiddenNotableDateKeys, setHiddenNotableDateKeys] = useState(() => new Set());
  const [hoveredNotableDateKey, setHoveredNotableDateKey] = useState(null);
  const [removingNotableDateKey, setRemovingNotableDateKey] = useState(null);
  const [selectedPoint, setSelectedPoint] = useState(null);

  const chartData = useMemo(() => (
    (Array.isArray(prices) ? prices : [])
      .reduce((rows, price) => {
        const dateMs = parseDateMs(price?.trading_date);
        const adjustedClose = toFiniteNumber(price?.adj_close);
        const close = toFiniteNumber(price?.close);
        const plottedPrice = preferAdjustedClose && adjustedClose !== null ? adjustedClose : close;

        if (!price?.trading_date || dateMs === null || plottedPrice === null) {
          return rows;
        }

        rows.push({
          date: price?.trading_date,
          dateMs,
          price: plottedPrice,
          close,
          adj_close: adjustedClose,
          volume: price?.volume ?? null,
        });

        return rows;
      }, [])
  ), [preferAdjustedClose, prices]);
  const statusCopy = getStatusCopy(priceStatus, priceMessage, redactionEnabled);
  const firstChartDate = chartData[0]?.date;
  const lastChartDate = chartData[chartData.length - 1]?.date;
  const priceLabel = preferAdjustedClose ? 'Adjusted close' : 'Close';
  const referenceTimestamp = parseDateMs(referenceDate);
  const notableDateMarkers = useMemo(() => (
    (Array.isArray(notableDates) ? notableDates : [])
      .map(normalizeNotableDateMarker)
      .filter(Boolean)
      .toSorted((a, b) => a.timestamp - b.timestamp)
  ), [notableDates]);
  const visibleNotableDateMarkers = useMemo(() => (
    notableDateMarkers.filter((marker) => !hiddenNotableDateKeys.has(marker.key))
  ), [hiddenNotableDateKeys, notableDateMarkers]);
  // Keep all in-range markers mounted; hide/show toggles visibility without remounting the price line.
  const chartNotableDateMarkers = useMemo(() => {
    if (!chartData.length) {
      return [];
    }

    const priceTimestamps = chartData.map((price) => price.dateMs);
    const loadedRangeStart = Math.min(...priceTimestamps);
    const loadedRangeEnd = Math.max(...priceTimestamps);
    return notableDateMarkers.filter(
      (marker) => marker.timestamp >= loadedRangeStart && marker.timestamp <= loadedRangeEnd,
    );
  }, [chartData, notableDateMarkers]);
  const allNotableDatesHidden = notableDateMarkers.length > 0
    && visibleNotableDateMarkers.length === 0;
  const toggleNotableDate = useCallback((key) => {
    setHiddenNotableDateKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
    setHoveredNotableDateKey(null);
  }, []);
  const toggleAllNotableDates = useCallback(() => {
    setHiddenNotableDateKeys(
      allNotableDatesHidden
        ? new Set()
        : new Set(notableDateMarkers.map((marker) => marker.key)),
    );
    setHoveredNotableDateKey(null);
  }, [allNotableDatesHidden, notableDateMarkers]);
  const handleRemoveNotableDate = useCallback(async (marker) => {
    if (!onRemoveNotableDate || !marker?.id || removingNotableDateKey) {
      return;
    }

    setRemovingNotableDateKey(marker.key);
    setHoveredNotableDateKey(null);
    try {
      await onRemoveNotableDate(marker);
      setHiddenNotableDateKeys((current) => {
        if (!current.has(marker.key)) return current;
        const next = new Set(current);
        next.delete(marker.key);
        return next;
      });
    } finally {
      setRemovingNotableDateKey(null);
    }
  }, [onRemoveNotableDate, removingNotableDateKey]);
  const xDomain = useMemo(() => {
    const timestamps = chartData.map((price) => price.dateMs);
    if (referenceTimestamp !== null) {
      timestamps.push(referenceTimestamp);
    }

    if (!timestamps.length) {
      return ['dataMin', 'dataMax'];
    }

    return [Math.min(...timestamps), Math.max(...timestamps)];
  }, [chartData, referenceTimestamp]);

  const handlePointClick = useCallback((point) => {
    if (!onUseAsSnapshotDate) {
      return;
    }
    setSelectedPoint(point);
  }, [onUseAsSnapshotDate]);

  const handleDismissSelectedPoint = useCallback(() => {
    setSelectedPoint(null);
  }, []);

  const handleUseSelectedAsSnapshot = useCallback(() => {
    if (!selectedPoint?.date || !onUseAsSnapshotDate) {
      return;
    }
    onUseAsSnapshotDate(selectedPoint.date);
  }, [onUseAsSnapshotDate, selectedPoint]);

  useEffect(() => {
    setSelectedPoint(null);
  }, [prices, preferAdjustedClose]);

  const tooltipRenderer = useCallback(
    ({ active, payload }) => {
      if (!active || !payload?.length) return null;

      const point = payload[0]?.payload || {};
      const volume = formatVolume(point.volume);

      return (
        <Stack
          gap="1"
          bg={tooltipBg}
          border="1px solid"
          borderColor={tooltipBorder}
          borderRadius="md"
          boxShadow="lg"
          p="3"
          color={tooltipText}
        >
          <Text fontWeight="semibold">{point.date}</Text>
          {point.adj_close !== null && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Adjusted close</Text>
              <Text fontSize="sm" fontWeight="semibold">{formatTooltipPrice(point.adj_close)}</Text>
            </Box>
          )}
          {point.close !== null && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Closing price</Text>
              <Text fontSize="sm" fontWeight="semibold">{formatTooltipPrice(point.close)}</Text>
            </Box>
          )}
          {volume && (
            <Box display="flex" justifyContent="space-between" gap="4">
              <Text fontSize="sm" color={tooltipMuted}>Volume</Text>
              <Text fontSize="sm" fontWeight="semibold">{volume}</Text>
            </Box>
          )}
          {onUseAsSnapshotDate ? (
            <Text fontSize="xs" color={tooltipMuted} pt="1">
              Click to pin this date for Snapshot
            </Text>
          ) : null}
        </Stack>
      );
    },
    [onUseAsSnapshotDate, tooltipBg, tooltipBorder, tooltipMuted, tooltipText],
  );

  if (loading) {
    return (
      <Box
        minH="320px"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={panelBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="stock-price-history-loading"
      >
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error" borderRadius="md" data-testid="stock-price-history-error">
        <Alert.Indicator />
        <Alert.Content>{getErrorMessage(error)}</Alert.Content>
      </Alert.Root>
    );
  }

  if (!chartData.length) {
    return (
      <Box
        minH="220px"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="md"
        bg={emptyBg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        gap="1"
        px="4"
        textAlign="center"
        data-testid="stock-price-history-empty"
      >
        <Text color={mutedText} fontSize="sm" fontWeight="medium">
          {statusCopy?.label || (redactionEnabled
            ? 'No pre-snapshot price history'
            : 'No stock price history')}
        </Text>
        <Text color={mutedText} fontSize="xs">
          {statusCopy?.detail || 'No usable prices are available for this range.'}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      w="full"
      minH="320px"
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      bg={panelBg}
      p="3"
      data-testid="stock-price-history-chart"
    >
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between"
        align={{ base: 'flex-start', sm: 'center' }}
        gap="1"
        mb="2"
      >
        <Text fontSize="sm" fontWeight="semibold" color={tooltipText}>
          {priceLabel}
          {chartData.some((point) => Number.isFinite(Number(point.volume))) ? ' · Volume' : ''}
        </Text>
        {firstChartDate && lastChartDate ? (
          <Text fontSize="xs" color={mutedText} data-testid="stock-price-history-date-range">
            {formatDragCompareDate(firstChartDate)} to {formatDragCompareDate(lastChartDate)}
          </Text>
        ) : null}
      </Stack>
      {selectedPoint?.date && onUseAsSnapshotDate ? (
        <Box
          mb="3"
          px="3"
          py="2.5"
          borderWidth="1px"
          borderColor={snapshotPromptBorder}
          borderRadius="md"
          bg={snapshotPromptBg}
          data-testid="stock-price-snapshot-date-prompt"
        >
          <Box
            display="flex"
            alignItems={{ base: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            gap="3"
            flexWrap="wrap"
          >
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color={tooltipText}>
                {formatDragCompareDate(selectedPoint.date) || selectedPoint.date}
              </Text>
              <Text fontSize="xs" color={mutedText}>
                {formatTooltipPrice(selectedPoint.price)}
                {formatVolume(selectedPoint.volume)
                  ? ` · Vol ${formatVolume(selectedPoint.volume)}`
                  : ''}
              </Text>
            </Box>
            <Box display="flex" alignItems="center" gap="2">
              <Button
                type="button"
                size="sm"
                colorPalette="teal"
                onClick={handleUseSelectedAsSnapshot}
              >
                Use as snapshot history
              </Button>
              <IconButton
                type="button"
                size="sm"
                variant="ghost"
                aria-label="Dismiss selected date"
                onClick={handleDismissSelectedPoint}
              >
                <LuX />
              </IconButton>
            </Box>
          </Box>
        </Box>
      ) : null}
      {statusCopy ? (
        <Box
          borderWidth="1px"
          borderColor={statusCopy.tone === 'orange' ? 'orange.200' : borderColor}
          borderRadius="md"
          bg={statusCopy.tone === 'orange' ? 'orange.50' : emptyBg}
          px="3"
          py="2"
          mb="3"
          data-testid="stock-price-history-status"
        >
          <Text fontSize="xs" fontWeight="semibold" color={tooltipText}>
            {statusCopy.label}
          </Text>
          <Text fontSize="xs" color={mutedText}>
            {statusCopy.detail}
          </Text>
        </Box>
      ) : null}
      <Suspense
        fallback={
          <Box h={{ base: '260px', md: '320px' }} display="flex" alignItems="center" justifyContent="center">
            <Spinner size="lg" />
          </Box>
        }
      >
        <Box h={{ base: '260px', md: '320px' }}>
          <StockPriceHistoryRechartsShell
            chartData={chartData}
            gridStroke={gridStroke}
            axisColor={axisColor}
            formatPriceTick={formatPriceTick}
            formatDateTick={formatDateTick}
            formatVolumeTick={formatVolumeTick}
            tooltipRenderer={tooltipRenderer}
            referenceDate={referenceDate}
            referenceLabel={referenceLabel}
            referenceTimestamp={referenceTimestamp}
            selectedPoint={onUseAsSnapshotDate ? selectedPoint : null}
            notableDateMarkers={chartNotableDateMarkers}
            hiddenNotableDateKeys={hiddenNotableDateKeys}
            hoveredNotableDateKey={hoveredNotableDateKey}
            onNotableDateHover={setHoveredNotableDateKey}
            onPointClick={onUseAsSnapshotDate ? handlePointClick : null}
            xDomain={xDomain}
            lineType={lineType}
          />
        </Box>
      </Suspense>
      {notableDateMarkers.length > 0 ? (
        <Stack gap="2" mt="4" data-testid="notable-date-list">
          <Box display="flex" alignItems="center" justifyContent="space-between" gap="3">
            <Box>
              <Text fontSize="sm" fontWeight="bold" color={tooltipText}>Notable dates</Text>
              <Text fontSize="xs" color={mutedText}>Hover an event to highlight it on the chart.</Text>
            </Box>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              colorPalette="purple"
              onClick={toggleAllNotableDates}
              aria-label={allNotableDatesHidden ? 'Show all notable dates' : 'Hide all notable dates'}
            >
              {allNotableDatesHidden ? <LuEye /> : <LuEyeOff />}
              {allNotableDatesHidden ? 'Show all' : 'Hide all'}
            </Button>
          </Box>
          {notableDateMarkers.map((marker) => (
            <Box
              key={marker.key}
              display="grid"
              gridTemplateColumns="auto minmax(0, 1fr) auto"
              alignItems="start"
              gap="2"
              px="2"
              py="2"
              borderRadius="md"
              bg={hoveredNotableDateKey === marker.key ? notableHoverBg : 'transparent'}
              opacity={hiddenNotableDateKeys.has(marker.key) ? 0.58 : 1}
              transition="background-color 120ms ease, opacity 120ms ease"
              fontSize="sm"
              color={tooltipText}
              tabIndex={0}
              data-testid={`notable-date-${marker.key}`}
              onMouseEnter={() => {
                if (!hiddenNotableDateKeys.has(marker.key)) setHoveredNotableDateKey(marker.key);
              }}
              onMouseLeave={() => setHoveredNotableDateKey(null)}
              onFocus={() => {
                if (!hiddenNotableDateKeys.has(marker.key)) setHoveredNotableDateKey(marker.key);
              }}
              onBlur={() => setHoveredNotableDateKey(null)}
              _hover={{ bg: hiddenNotableDateKeys.has(marker.key) ? notableMutedBg : notableHoverBg }}
              _focusVisible={{ outline: '2px solid', outlineColor: 'purple.400' }}
            >
              <Box display="flex" alignItems="center" gap="2" minW="7rem" pt="0.5">
                <Box
                  w="8px"
                  h="8px"
                  borderRadius="full"
                  bg={hiddenNotableDateKeys.has(marker.key) ? 'gray.400' : '#805AD5'}
                  flexShrink={0}
                />
                <Text as="span" color={mutedText} whiteSpace="nowrap">
                  {marker.event_date}
                </Text>
              </Box>
              {marker.filing_url ? (
                <Link
                  href={marker.filing_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  color={hiddenNotableDateKeys.has(marker.key) ? mutedText : 'blue.600'}
                  fontWeight="medium"
                  lineHeight="1.45"
                >
                  {marker.description}
                </Link>
              ) : (
                <Text as="span" fontWeight="medium" lineHeight="1.45">{marker.description}</Text>
              )}
              <Box display="flex" alignItems="center" gap="0.5" pt="0.5">
                <UiTooltip
                  content={hiddenNotableDateKeys.has(marker.key) ? 'Show on chart' : 'Hide from chart'}
                  positioning={{ placement: 'left' }}
                >
                  <IconButton
                    type="button"
                    size="xs"
                    variant="ghost"
                    colorPalette="purple"
                    aria-label={hiddenNotableDateKeys.has(marker.key)
                      ? `Show notable date ${marker.event_date}`
                      : `Hide notable date ${marker.event_date}`}
                    onClick={() => toggleNotableDate(marker.key)}
                  >
                    {hiddenNotableDateKeys.has(marker.key) ? <LuEyeOff /> : <LuEye />}
                  </IconButton>
                </UiTooltip>
                {onRemoveNotableDate ? (
                  <UiTooltip
                    content="Remove from timeline"
                    positioning={{ placement: 'left' }}
                  >
                    <IconButton
                      type="button"
                      size="xs"
                      variant="ghost"
                      colorPalette="red"
                      aria-label={`Remove notable date ${marker.event_date} from timeline`}
                      disabled={removingNotableDateKey === marker.key}
                      onClick={() => handleRemoveNotableDate(marker)}
                    >
                      <LuX />
                    </IconButton>
                  </UiTooltip>
                ) : null}
              </Box>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
